/**
 * REVCLOUD entity → `RenderableEntity` adapter (TYPED).
 *
 * DEFUSE Direction-3 — typed sibling of `../revisionCloudToRenderable.ts`.
 * Reads the canonical `RevisionCloudEntity` fields directly off the model
 * (`model.ts`: `revisionCloudEntitySchema` → `vertices` / `closed` /
 * `approximateArcLength`) instead of the legacy `EntityPreview.properties`
 * string bag, then feeds the SAME shared builder
 * (`../geom/revisionCloudGeometry.ts`) to produce a byte-identical
 * `RenderableEntity` (golden-parity) for the same entity.
 *
 * # Field mapping (model → builder input)
 *
 *   - `entity.vertices`             → raw (pre-offset) `Vec3[]`. Each
 *     `polylineVertexSchema` vertex carries `x` / `y` only (no z); the legacy
 *     bag (`__revcloud_vertices`) likewise drops z and `readVertices` reads
 *     `Number(obj.x)` / `Number(obj.y)` with z = 0. We mirror that exactly:
 *     `{ x, y, z: 0 }`. The builder applies `applyOffset` per vertex.
 *   - `entity.closed`              → `closed` flag. The legacy bag stores
 *     `Closed: entity.closed ? "true" : "false"` and the legacy adapter parses
 *     `String(... ?? "true").toLowerCase() === "true"`; for the typed model the
 *     boolean is read directly (default `true` from the schema).
 *   - `entity.approximateArcLength`→ arc length. The legacy producer writes
 *     `formatNumber(entity.approximateArcLength ?? 15)` into the bag and the
 *     legacy adapter reads it back with a `15` fallback; the typed model field
 *     defaults to `15` via the schema, so reading it directly matches. The
 *     shared builder applies the same `Math.max(1, …)` floor both paths used.
 *
 * Returns `null` when the cloud is degenerate (< 2 vertices / < 2 sampled
 * points), exactly like the legacy adapter — the builder's `null` is surfaced
 * verbatim so the caller skips the entity identically.
 *
 * Category B: standalone file; NOT imported into any render path yet (the typed
 * DISPATCHER case is added in a later slice).
 */

import type { RevisionCloudEntity } from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import { buildRenderableShell, type AdapterContext } from "../adapterContext";
import { buildRevisionCloudSceneGeometry } from "../geom/revisionCloudGeometry";

/** Typed adapter for the `Revision Cloud` entity (`revisionCloudEntitySchema`). */
export function revisionCloudFromEntity(
  entity: RevisionCloudEntity,
  ctx: AdapterContext,
): RenderableEntity | null {
  // `polylineVertexSchema` vertices carry x/y only; the entity-level
  // `elevation` (DXF group 38 — REVCLOUD is an LWPOLYLINE on disk) is the
  // common z of every vertex, exactly as `lwpolylineFromEntity` reads it
  // (@codex #431 round-2 Z sweep: the block flatten z-collapses `elevation`,
  // but this adapter rebuilt every vertex at z 0 and never read the field).
  // DOCUMENTED SUPERSET vs legacy: the bag path (`__revcloud_vertices`)
  // drops z AND never reads `Elevation`, so legacy always renders at 0 —
  // identical output for the schema-default elevation 0 (golden parity),
  // MORE faithful for an authored group 38 (same class as the LWPOLYLINE
  // elevation superset).
  const elevation = Number.isFinite(entity.elevation ?? 0)
    ? (entity.elevation ?? 0)
    : 0;
  const verticesRaw: Vec3[] = entity.vertices.map((v) => ({
    x: v.x,
    y: v.y,
    z: elevation,
  }));

  // Read the typed fields directly. Both default to the schema values
  // (`closed` → true, `approximateArcLength` → 15) — identical to the legacy
  // bag defaults — and the shared builder floors the arc length at 1.
  const result = buildRevisionCloudSceneGeometry(
    verticesRaw,
    entity.closed,
    entity.approximateArcLength,
  );
  if (!result) return null;

  return buildRenderableShell(entity, ctx, result.geometry, result.bounds);
}
